import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CORPORATE_HOMES } from "@/lib/constants";

const POINTS = [
  "Møblerede boliger - klar til indflytning",
  "Ét fast kontaktpunkt fra forespørgsel til udflytning",
  "Fleksibel opholdslængde - fra få uger til flere måneder",
];

export default function CorporateCta() {
  const home = CORPORATE_HOMES.find((h) => h.slug === "baelum") || CORPORATE_HOMES[0];
  /* Professional interior shot (Bælum dining room) instead of the cover photo */
  const image =
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-hosting-2/artifacts/arwrmw8v_zhenfei%20bondeg%C3%A5rd%20billede%2020%20%28optimeret%29.png";
  return (
    <section
      id="virksomheder"
      data-testid="corporate-cta-section"
      className="relative bg-[#111111] text-white overflow-hidden"
    >
      <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] glow-gold pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={image}
                alt={home.title}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/40 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="absolute -bottom-4 left-4 sm:-left-4 bg-[#D4AF37] text-[#111111] px-4 py-3 shadow-2xl">
              <div className="text-[9px] tracking-[0.3em] uppercase opacity-70">Firmabolig</div>
              <div
                className="font-serif text-base sm:text-lg leading-none mt-1"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                {home.shortTitle}
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: "easeOut" }}
            className="lg:col-span-6"
          >
            <p className="overline mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Til virksomheder
            </p>
            <h2
              className="font-serif text-[2.25rem] sm:text-5xl lg:text-[3.75rem] text-white font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Bolig til dine <span className="italic text-[#D4AF37]">medarbejdere?</span>
            </h2>
            <p className="mt-6 text-white/75 text-base sm:text-lg font-light leading-relaxed max-w-xl">
              Vi tilbyder møblerede boliger til virksomheder med medarbejdere,
              konsulenter og projektansatte, der har behov for midlertidig
              indkvartering.
            </p>

            <ul className="mt-8 space-y-3 max-w-xl">
              {POINTS.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 text-white/85 text-sm sm:text-base font-light leading-relaxed"
                >
                  <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <Link
                to="/firmaboliger"
                data-testid="corporate-cta-btn"
                className="btn-gold group"
                style={{ background: "#D4AF37", borderColor: "#D4AF37", color: "#111111" }}
              >
                <span className="inline-flex items-center gap-2.5">
                  Se firmaboliger
                  <ArrowRight
                    size={14}
                    strokeWidth={1.5}
                    className="transition-transform duration-500 group-hover:translate-x-1"
                  />
                </span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
